import { LottieFileTypes } from "@/api/AppApi/services/company/interfaces/ICompany";
import { useEffect, useState } from "react";
import AuthButton from "../AuthButton";
import AuthContainer from "../auth-container/AuthContainer";
import InputArray from "./confirmation-helpers/InputArray";
import SvgResendCodeTimer from "./confirmation-helpers/SvgResendCodeTimer";
import LottieFileInPage from "@/app/components/lib/lottie-files/LottieFileInPage";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { JSX } from "react";
const codeLength = 5;
enum BtnStates {
  confirm,
  confirmDisabled,
  resendError,
  resend,
  resendDisabled,
  timerdisabled,
}
export const resendPeriod = 480;

interface Props {
  byEmail: boolean;
  title: string;
  subtitle: string;
  setCode: (code: string) => void;
  sendCode: () => void;
  verifyCode: () => void;
  isLoading: boolean;
  isError: boolean;
  counter: number;
  setCounter: (counter: number) => void;
  redirectTo?: JSX.Element;
}

const CodeForm: React.FC<Props> = ({
  byEmail,
  title,
  subtitle,
  setCode,
  sendCode,
  verifyCode,
  isLoading,
  isError,
  counter,
  setCounter,
  redirectTo = null,
}) => {
  const t = useTranslation();

  const [clear, setClear] = useState(false);
  const [btnStatus, setBtnStatus] = useState<BtnStates>(
    BtnStates.timerdisabled,
  );

  const handleClick = () => {
    if (btnStatus === BtnStates.confirm) verifyCode();
    else {
      setClear(true);
      sendCode();
      setCounter(-1); //unstable value waiting resendCode
      setBtnStatus(BtnStates.timerdisabled);
    }
  };

  const hangleCodeChange = (value: string) => {
    setCode(value);
    if (value.length === 0) {
      setBtnStatus(counter === 0 ? BtnStates.resend : BtnStates.timerdisabled);
    } else if (value.length === codeLength) setBtnStatus(BtnStates.confirm);
    else {
      setBtnStatus(BtnStates.confirmDisabled);
    }
  };

  const btnCaption = () => {
    const shapeCounter = () => {
      if (counter > 60) {
        return `
          ${Math.floor(counter / 60)
            ?.toString()
            .padStart(2, "0")}
          :${(counter % 60)?.toString().padStart(2, "0")}
          `;
      }
      return counter?.toString().padStart(2, "0");
    };
    if (isLoading) return "";
    switch (btnStatus) {
      case BtnStates.confirm:
      case BtnStates.confirmDisabled:
        return t.confirm;
      case BtnStates.timerdisabled:
        return (
          <bdi>
            <SvgResendCodeTimer />
            <>{shapeCounter()}</>
          </bdi>
        );
      case BtnStates.resend:
        return t["didn't_get_the_code"];
      case BtnStates.resendDisabled:
        return (
          <>
            {t.wrong_code} <bdi>{shapeCounter()}</bdi>
          </>
        );
      case BtnStates.resendError:
        return t.wrong_code;
    }
  };

  useEffect(() => {
    let t: NodeJS.Timeout;
    if (counter === 0) {
      if (btnStatus === BtnStates.resendDisabled)
        setBtnStatus(BtnStates.resendError);
      else if (btnStatus === BtnStates.timerdisabled)
        setBtnStatus(BtnStates.resend);
    } else t = setTimeout(() => setCounter(counter - 1), 1000);
    return () => {
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter]);

  useEffect(() => {
    if (!isError) return;
    setBtnStatus(
      counter === 0 ? BtnStates.resendError : BtnStates.resendDisabled,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  useEffect(() => {
    setClear(true);
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byEmail]);

  return (
    <AuthContainer title={t.confirmation}>
      <LottieFileInPage
        lottieFileType={
          byEmail
            ? LottieFileTypes.email_verification
            : LottieFileTypes.mobile_verification
        }
        title={title}
        text={subtitle}
      />
      <InputArray
        clear={clear}
        setClear={setClear}
        count={codeLength}
        setCode={hangleCodeChange}
        danger={
          btnStatus === BtnStates.resendError ||
          btnStatus === BtnStates.resendDisabled
        }
      />
      <AuthButton
        disabled={
          btnStatus === BtnStates.timerdisabled ||
          btnStatus === BtnStates.confirmDisabled ||
          btnStatus === BtnStates.resendDisabled
        }
        error={btnStatus === BtnStates.resendError}
        onClick={handleClick}
        isLoading={isLoading}
      >
        {btnCaption()}
      </AuthButton>
      {redirectTo ? redirectTo : <></>}
    </AuthContainer>
  );
};

export default CodeForm;
